import {HttpVerb} from "../../../types/http-verb";
import {IDotRezGraphQLQuery} from "../graph-ql/dot-rez-graph-ql-query.interface";
import {IServiceFactory} from "../../service-factory.interface";
import {IDotRezSessionTimerModel} from "./session-timer/dot-rez-session-timer.interface";
import {isHttpErrorStatusCode} from "../../http/http-helpers";
import {DotRezResponse} from "./dot-rez-response";
import {IDotRezAvailabilitySimpleRequest} from "../data-contracts/requests/booking/search-simple.request";
import {IDotRezSimpleAvailability} from "../data-contracts/booking/search-simple/search-simple.data-contracts";
import {IDotRezAvailabilityLowFareRequest} from "../data-contracts/requests/booking/lowfare-search.request";
import {IDotRezAvailabilityLowFareResponse} from "../data-contracts/responses/booking/lowfare-search.response";

interface IFetchOptions {
    body?: any;
    doNotThrowOnError?: boolean;
}

export abstract class DotRezSessionBase {
    constructor(public readonly token: string,
                public readonly services: IServiceFactory,
                sessionTimer: IDotRezSessionTimerModel) {
        this._sessionTimer = sessionTimer;
    }

    private _buildUrl(path: string): string {
        return this.services.dotRezApi.buildDotRezUrl(path);
    }

    protected _sessionTimer: IDotRezSessionTimerModel;

    protected async _fetchData<TData>(method: HttpVerb, path: string, options?: IFetchOptions): Promise<DotRezResponse<TData>> {

        this._sessionTimer.refresh();

        const url = this._buildUrl(path);
        const fetchResponse = await fetch(url, {
            method: method,
            body: options?.body && JSON.stringify(options.body),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': this.token,
                ...this.services.configuration.dotRezApiSubscriptionKeyHeader
            }
        });

        const json = await fetchResponse.json();
        if(isHttpErrorStatusCode(fetchResponse.status)) {
            const errorMsg = `${method} ${path} failed with status ${fetchResponse.status} - ${fetchResponse.statusText}`;
            this.services.logger.error(errorMsg, {
                errors: json.errors,
                request: options?.body
            });
        }

        const dotRezResponse = new DotRezResponse<TData>(fetchResponse.status, json.data, fetchResponse.headers, json.messages, json.errors);

        if(!options?.doNotThrowOnError) {
            dotRezResponse.throwIfError();
        }

        return dotRezResponse;
    }

    protected async get<TData>(path: string, options?: IFetchOptions): Promise<DotRezResponse<TData>> {
        return await this._fetchData('GET', path, options);
    }

    protected async post<TData>(path: string, options?: IFetchOptions): Promise<DotRezResponse<TData>> {
        return await this._fetchData('POST', path, options);
    }

    protected async put<TData>(path: string, options?: IFetchOptions): Promise<DotRezResponse<TData>> {
        return await this._fetchData('PUT', path, options);
    }

    protected async patch<TData>(path: string, options?: IFetchOptions): Promise<DotRezResponse<TData>> {
        return await this._fetchData('PATCH', path, options);
    }

    protected async delete<TData>(path: string, options?: IFetchOptions): Promise<DotRezResponse<TData>> {
        return await this._fetchData('DELETE', path, options);
    }


    protected async executeGraphQLMutations(mutations: IDotRezGraphQLQuery[]): Promise<void> {
        for(let mutation of mutations) {
            await this._graphQL(mutation);
        }
    }

    async executeGraphQLQuery<TData = any>(query: IDotRezGraphQLQuery): Promise<DotRezResponse<TData>> {
        return await this._graphQL<TData>(query);
    }

    protected async _graphQL<TData>(query: IDotRezGraphQLQuery, options?: Omit<IFetchOptions, 'body'>): Promise<DotRezResponse<TData>> {
        return await this.post<TData>(`/api/v2/graph/${query.queryName}`, {
            body: query.request,
            ...options
        });
    }

    async availabilityLowFare(request: IDotRezAvailabilityLowFareRequest): Promise<IDotRezAvailabilityLowFareResponse> {
        const {classesOfService, ...restOfTheParams} = {...request};
        if((classesOfService?.length ?? 0) === 0) {
            const response = await this.post<IDotRezAvailabilityLowFareResponse>('/api/nsk/v2/availability/lowfare/simple', {
                body: restOfTheParams
            });
            return response.data;
        }

        const response = await this.post<IDotRezAvailabilityLowFareResponse>('/api/nsk/v2/availability/lowfare', {
            body: {
                passengers:{
                    types: request.passengers
                },
                codes: {
                    currency: request.currencyCode
                },
                filters: {
                    bookingClasses: classesOfService
                },
                criteria: [
                    {
                        originStationCodes: [request.origin],
                        destinationStationCodes: [request.destination],
                        beginDate: request.beginDate,
                        endDate: request.endDate
                    }
                ]
            }
        });
        return response.data;
    }


    async availabilitySimple(request: IDotRezAvailabilitySimpleRequest): Promise<IDotRezSimpleAvailability> {
        const response = await this.post<IDotRezSimpleAvailability>('/api/nsk/v4/availability/search/simple', {
            body: request
        });

        return response.data;
    }
}
